// apps/web/src/features/crm/leads/card/LeadCardStageRail.tsx
//
// Row-3 stage rail of the card header (#2308, #2247 FR-3). The linear funnel
// (new → inWork → closing → deal) renders through the shared `StagePath`
// primitive via `leadStagePathStages`. A terminal projection
// (NON_TARGET / REFUSED) is NOT a linear path — it renders the read-only
// funnel-state badge plus a single gated back-to-work pill (also a `StagePath`
// so the blocked/available treatment + lock tooltip match the funnel exactly).
// Every move routes through the reused legacy stage mutation (`onTransition`);
// its own `isPending` guard makes the pills double-submit-safe, so no `busy`
// flag is threaded here.
import { useTranslation } from 'react-i18next';
import {
  EngagementPhase,
  EngagementStage,
  isTerminalEngagementStage,
  type LeadStageCode,
} from '@magimp/shared';
import { StagePath, type StagePathStage } from '../../../../ui/index.js';
import { EngagementStageBadge } from '../list/EngagementStageBadge.js';
import { leadStagePathStages } from './leadStagePath.js';

export interface LeadCardStageRailProps {
  phase: EngagementPhase;
  stage: EngagementStage;
  hasClient: boolean;
  canManage: boolean;
  onTransition: (toStage: LeadStageCode) => void;
}

export function LeadCardStageRail({
  phase,
  stage,
  hasClient,
  canManage,
  onTransition,
}: LeadCardStageRailProps) {
  const { t } = useTranslation(['leads']);

  if (isTerminalEngagementStage(stage)) {
    const locked = !hasClient;
    const backToWork: StagePathStage = locked
      ? {
          key: 'backToWork',
          label: t('leads:card.actions.backToWork'),
          state: 'blocked',
          blockedReason: t('leads:card.inWorkLocked'),
        }
      : {
          key: 'backToWork',
          label: t('leads:card.actions.backToWork'),
          state: 'available',
          onSelect: () => onTransition('in_work'),
        };
    return (
      <div
        data-testid="lead-stage-rail-terminal"
        style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
      >
        <EngagementStageBadge phase={phase} stage={stage} />
        {canManage && <StagePath stages={[backToWork]} />}
      </div>
    );
  }

  const stages = leadStagePathStages({
    phase,
    stage,
    hasClient,
    canManage,
    labels: {
      new: t('leads:card.funnel.new'),
      inWork: t('leads:card.funnel.inWork'),
      closing: t('leads:card.funnel.closing'),
      deal: t('leads:card.funnel.deal'),
    },
    inWorkLockedReason: t('leads:card.inWorkLocked'),
    run: onTransition,
  });

  return (
    <div data-testid="lead-stage-pills">
      <StagePath stages={stages} />
    </div>
  );
}
